import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

/**
 * SEO Component for managing meta tags and structured data
 * Used across feature pages and main pages for optimal search engine ranking
 */
// The browser-tab title. Pages that already end theirs with "| Canira" (eleven
// did) don't get the site name twice.
export function seoTitle(title, siteName = 'Canira') {
  if (!title) return siteName;
  const t = String(title).trim();
  return t.endsWith(`| ${siteName}`) ? t : `${t} | ${siteName}`;
}

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  // Optional MIME + dimensions for og:image. Needed when ogImage is
  // an SVG so platforms (Facebook in particular) know how to render
  // it — without an explicit type, several social previewers fall
  // back to the default favicon for non-PNG/JPG og images. Defaults
  // are sensible for the Hero Section PNG used as the page-wide
  // fallback; pages that ship their own ogImage (especially SVG)
  // should pass these too.
  ogImageType,
  ogImageWidth,
  ogImageHeight,
  ogType = 'website',
  structuredData,
  noIndex = false,
  // Branding overrides — default to Canira. Full white-label surfaces (a
  // Studio-tier host's clip page) pass their own so the tab title, og:site_name
  // and favicon carry the host's brand, not Canira's. Driving these through
  // Helmet (rather than an imperative document.title/favicon effect) keeps a
  // single source of truth — an effect race with Helmet was silently
  // re-appending "| Canira" to white-label tabs.
  siteName = 'Canira',
  favicon
}) => {
  const tagline = 'Turn one workshop into a week of marketing.';
  // Default social preview image for any page that renders <SEO /> without
  // its own ogImage: frontend/public/og-default.png (1200x630), the same
  // card as index.html and api/og.js.
  const defaultImage = '/og-default.png?v=2';
  const baseUrl = 'https://www.canira.io';

  const fullTitle = seoTitle(title, siteName);
  const fullCanonicalUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  const fullOgImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`)
    : `${baseUrl}${defaultImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonicalUrl} />
      {/* White-label favicon override (host logo). Helmet swaps it in while
          this page is mounted and restores the default on unmount. */}
      {favicon && <link rel="icon" href={favicon} />}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      {ogImageType && <meta property="og:image:type" content={ogImageType} />}
      {ogImageWidth && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {ogImageHeight && <meta property="og:image:height" content={String(ogImageHeight)} />}
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Structured Data / JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * SEO Provider wrapper - wrap your app with this
 */
export const SEOProvider = ({ children }) => (
  <HelmetProvider>{children}</HelmetProvider>
);

/**
 * Generate WebApplication structured data for software pages
 */
export const generateSoftwareSchema = ({
  name,
  description,
  applicationCategory,
  offers,
  aggregateRating
}) => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": name,
  "description": description,
  "applicationCategory": applicationCategory || "BusinessApplication",
  "operatingSystem": "Web Browser",
  "offers": offers || {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  ...(aggregateRating && { aggregateRating })
});

/**
 * Generate FAQ structured data
 */
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Generate Organization structured data
 */
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Canira",
  "url": "https://www.canira.io",
  "logo": "https://www.canira.io/logo.png",
  "description": "Creator media infrastructure — one AI-powered platform for broadcasting, webinars, podcasts, courses, and monetization.",
  "sameAs": [
    "https://www.instagram.com/canira.io/",
    "https://www.facebook.com/canira.io",
    "https://www.linkedin.com/company/canira-inc"
  ]
});

/**
 * Generate BlogPosting structured data for a single founder-log post.
 *
 * This is the highest-leverage markup for GEO / answer-engine citations:
 * it tells ChatGPT, Perplexity, Gemini, and Google AI Overviews exactly
 * who authored the piece, when, and what it's about — so they can quote
 * and attribute it back to Canira. Keep author + publisher entities
 * consistent across posts so models build a confident "Canira = X"
 * association.
 *
 * @param {object} post - a BLOG_POSTS entry (slug, title, excerpt,
 *   publishedAt, author, authorRole, tags)
 */
export const generateArticleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": post.title,
  "description": post.excerpt,
  "datePublished": post.publishedAt,
  "dateModified": post.publishedAt,
  "url": `https://www.canira.io/blog/${post.slug}`,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://www.canira.io/blog/${post.slug}`
  },
  "author": {
    "@type": "Person",
    "name": post.author || "Chante Earl",
    "jobTitle": post.authorRole || "Founder, Canira",
    "url": "https://www.canira.io/about"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Canira",
    "url": "https://www.canira.io",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.canira.io/logo.png"
    }
  },
  ...(post.tags && post.tags.length > 0 && { "keywords": post.tags.join(", ") })
});

/**
 * Generate Blog (collection) structured data for the founder-log index.
 * Lists the live posts as blogPost entries so answer engines can see the
 * full body of work and its cadence at a glance.
 *
 * @param {object[]} posts - the live BLOG_POSTS entries shown on /blog
 */
export const generateBlogSchema = (posts = []) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "Canira Founder Log",
  "description": "Notes from building Canira — live broadcasting infrastructure for broadcasters, pastors, coaches, and educators.",
  "url": "https://www.canira.io/blog",
  "publisher": {
    "@type": "Organization",
    "name": "Canira",
    "url": "https://www.canira.io"
  },
  "blogPost": posts.map((post) => ({
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.publishedAt,
    "url": `https://www.canira.io/blog/${post.slug}`,
    "author": {
      "@type": "Person",
      "name": post.author || "Chante Earl"
    }
  }))
});

export default SEO;
